import { SubmissionError } from "redux-form";

import { Typography } from "@braintrust/braintrust-ui-components";
import { Modal } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import type { StripeACHPayment } from "@js/types/payments";
import { getFormSubmitError } from "@js/utils/redux-form";

import {
  useVerifyStripeACHPaymentMethodMutation,
  useVerifyStripeACHPaymentMethodWithDescriptorCodeMutation,
} from "../../api";
import type {
  VerifyStripeACHFormData,
  VerifyStripeACHFormDataWithDescriptorCode,
} from "../../forms/verify-stripe-ach-payment-method";
import {
  VerifyStripeACHForm,
  VerifyStripeACHFormWithDescriptorCode,
} from "../../forms/verify-stripe-ach-payment-method";

import styles from "./style.module.scss";

const VERIFY_PAYMENT_METHOD_MODAL_ID = "verify-payment-method";

export const VerifyStripeACHPaymentMethodModalInstance = Modal(
  VERIFY_PAYMENT_METHOD_MODAL_ID,
);

type VerifyStripeACHPaymentMethodModalProps = {
  paymentMethod: StripeACHPayment;
  disableSnackbarOnSuccess?: boolean;
  onVerify?: () => void;
};

const VerifyStripeACHPaymentMethodModal = ({
  paymentMethod,
  disableSnackbarOnSuccess = false,
  onVerify,
}: VerifyStripeACHPaymentMethodModalProps) => {
  const [verifyStripeACHPayment] = useVerifyStripeACHPaymentMethodMutation();
  const [verifyStripeACHPaymentWithDescriptorCode] =
    useVerifyStripeACHPaymentMethodWithDescriptorCodeMutation();

  const handleOnSubmit = async (values: VerifyStripeACHFormData) => {
    try {
      await verifyStripeACHPayment({
        paymentMethodId: paymentMethod.method.id,
        values,
      }).unwrap();

      if (!disableSnackbarOnSuccess) {
        Snackbar.success("ACH payment method verified!");
      }
      VerifyStripeACHPaymentMethodModalInstance.close();
      onVerify?.();
    } catch (err) {
      const error = getFormSubmitError(err);
      throw new SubmissionError(error?.data as object);
    }
  };

  const handleOnSubmitWithDescriptorCode = async (
    values: VerifyStripeACHFormDataWithDescriptorCode,
  ) => {
    try {
      await verifyStripeACHPaymentWithDescriptorCode({
        paymentMethodId: paymentMethod.method.id,
        values,
      }).unwrap();

      if (!disableSnackbarOnSuccess) {
        Snackbar.success("ACH payment method verified!");
      }
      VerifyStripeACHPaymentMethodModalInstance.close();
      onVerify?.();
    } catch (err) {
      const error = getFormSubmitError(err);
      throw new SubmissionError(error?.data as object);
    }
  };

  return (
    <div className={styles.verifyPaymentMethodModal}>
      <Typography
        component="h1"
        variant="title"
        fontWeight={400}
        size="small"
        className="mt0"
        sx={{ mb: 2 }}
      >
        Verify Payment Method
      </Typography>
      {paymentMethod.content_type === "StripeACHPaymentMethod" &&
        paymentMethod.method.microdeposit_type ===
          ENUMS.ACHStripeMicrodepositTypes.AMOUNTS && (
          <>
            <Typography component="p">
              The payment method has to be verified before you can transfer the
              refundable deposit. To verify this payment method, you will need
              to enter in the value of the two small deposits. Two small
              deposits should be in the bank account selected in 1-2 days. If
              you are sending an offer to talent and using ACH, the offer will
              be sent once the total offer deposit has been collected. For
              immediate verification, enter in a credit card instead.
            </Typography>
            <VerifyStripeACHForm
              onSubmit={handleOnSubmit}
              onCancel={VerifyStripeACHPaymentMethodModalInstance.close}
            />
          </>
        )}
      {paymentMethod.content_type === "StripeACHPaymentMethod" &&
        paymentMethod.method.microdeposit_type ===
          ENUMS.ACHStripeMicrodepositTypes.DESCRIPTOR_CODE && (
          <>
            <Typography component="p">
              The payment method has to be verified before you can transfer the
              refundable deposit. To verify this payment method, you will need
              to enter the six-character code (starting with <strong>SM</strong>
              ) found in the microdeposit sent to your bank account.
              <br />
              The microdeposit should appear in the selected bank account within
              1–2 days. If you are sending an offer to talent and using ACH, the
              offer will be sent once the total offer deposit has been
              collected. For immediate verification, enter a credit card
              instead.
            </Typography>
            <VerifyStripeACHFormWithDescriptorCode
              onSubmit={handleOnSubmitWithDescriptorCode}
              onCancel={VerifyStripeACHPaymentMethodModalInstance.close}
            />
          </>
        )}
    </div>
  );
};

export const openVerifyStripeACHPaymentMethodModal = (
  props: VerifyStripeACHPaymentMethodModalProps,
) => {
  VerifyStripeACHPaymentMethodModalInstance.open({
    children: <VerifyStripeACHPaymentMethodModal {...props} />,
  });
};
